import axios from 'axios'
import { useMemo } from 'react'
import { FormInstance } from 'antd'
import { Dispatch } from 'redux'
import { NavigateFunction } from 'react-router-dom'

import {
    formatAuthResponse,
    setAuth,
    setAuthError,
    setMfaStatus,
    GRAPHQL_API_REST_BASE,
} from '~/redux-modules/auth'
import { SUCCESS } from '~/redux-modules/identity'
import { LOGIN_PATH } from '~/redux-modules/preferences'
import { AuthResponseType } from '~/redux-modules/auth/types'
import { trackIdentify } from '~/lib/analytics/actions/identify'
import isFlagEnabled from '~/lib/isFlagEnabled'
import LocalStorageWrapper from '~/lib/LocalStorageWrapper'
import { setUserContext } from '~/sentry'
import { VERIFICATION_CODE } from '../MyAccountPage/AccountSettings/constants'
import { CurrentUserIdentityDocument } from '~/gql/graphql'

import {
    ApolloClientType,
    LoginErrorResponseType,
    LoginErrorEnum,
    LoginErrorType,
} from './types'
import { PASSWORD, EMAIL_ADDRESS, PARAMS, SUCCESSFUL_LOGIN } from './constants'

const logUserIn = async ({
    dispatch,
    client,
    loginData,
    setLoading,
    mfaEnabled,
}: {
    dispatch: Dispatch
    client: ApolloClientType
    loginData: AuthResponseType
    setLoading: (loading: boolean) => void
    mfaEnabled?: boolean
}) => {
    dispatch(setAuth(loginData))

    const userIdentityResponse = await client.query({
        query: CurrentUserIdentityDocument,
    })

    if (userIdentityResponse?.data?.error) {
        throw userIdentityResponse.data.error
    }

    const { data: userIdentityData } = userIdentityResponse

    let identity = formatAuthResponse(userIdentityData, loginData.auth.idToken)
    const enforce2faEnabled = isFlagEnabled(identity, 'ENFORCE_2FA', 'true')

    if (identity.isAdmin && enforce2faEnabled && !mfaEnabled) {
        identity = {
            ...identity,
            mfaEnabled: false,
        }
        dispatch(setMfaStatus(false))
    }
    const { email, id } = identity
    const action = { type: SUCCESS, identity }

    if (mfaEnabled) {
        dispatch(setMfaStatus(mfaEnabled))
    }
    dispatch(trackIdentify(action, id, identity))
    setUserContext({ email, id })
    setLoading(false)
}

export const getLoginErrorType = (
    err: LoginErrorResponseType,
): LoginErrorType => {
    if (err.loginRemainingAttempts === null) {
        return {
            type: LoginErrorEnum.INCORRECT_EMAIL,
            err,
        }
    }

    if (err.isUserLockedOut) {
        return {
            type: LoginErrorEnum.LOCKED_OUT,
            err,
        }
    }

    if (err.loginRemainingAttempts === 1) {
        return {
            type: LoginErrorEnum.FINAL_LOCKOUT_WARNING,
            err,
        }
    }

    return {
        type: LoginErrorEnum.LOCKOUT_WARNING,
        err,
    }
}

export const handleSubmit = async ({
    form,
    setLoading,
    setLoginError,
    setHasError,
    setMfaEnabled,
    options,
    setSession,
    setEmail,
    navigate,
}: {
    form: FormInstance
    setLoading: (loading: boolean) => void
    setLoginError: (loginError: LoginErrorType) => void
    setHasError: (hasError: boolean) => void
    setMfaEnabled: (mfaEnabled: boolean) => void
    setSession: (session: string) => void
    setEmail: (email: string) => void
    options: {
        dispatch: Dispatch
        client: ApolloClientType
    }
    navigate: NavigateFunction
}): Promise<void> => {
    const storage = new LocalStorageWrapper()

    try {
        await form.validateFields([EMAIL_ADDRESS, PASSWORD])
    } catch (err) {
        return
    }
    setLoading(true)

    const { dispatch, client } = options
    const values = {
        email: form.getFieldValue(EMAIL_ADDRESS),
        password: form.getFieldValue(PASSWORD),
    }

    try {
        const loginResponse = await axios.post(
            `${GRAPHQL_API_REST_BASE}${LOGIN_PATH}`,
            values,
            { withCredentials: true },
        )

        if (loginResponse?.data?.error) {
            throw loginResponse.data.error
        }

        if (
            loginResponse?.data?.authVersion === 2 &&
            loginResponse?.data?.status !== 'VERIFIED'
        ) {
            // redirect to new page
            /**
             * VERIFIED if user is email verified, should allow the existing redirect logic to platform to occur as platform cookies will be sent
                VERIFICATION_SENT if their verification token is expired. we try to auto-send a new one rather than making them dig up the previous email and manually resend from there
                RESEND_ERROR if resending fails for any reason
                PENDING_VERIFICATION if a they have a non-expired verification waiting
             */
            navigate(`/verifyEmail`)
            return
        }

        const { data: loginData } = loginResponse

        if (loginData?.auth?.is2faEnabled && loginData?.auth?.session) {
            setMfaEnabled(true)
            setSession(loginData.auth.session)
            setEmail(values.email)
        } else {
            logUserIn({
                dispatch,
                client,
                loginData,
                setLoading,
            })
            storage.setItem(SUCCESSFUL_LOGIN, true)
        }
    } catch (err) {
        const authErrorMessage = (err as LoginErrorResponseType).message

        dispatch(setAuthError(`LoginPage:: ${authErrorMessage}`))
        setLoginError(getLoginErrorType(err as LoginErrorResponseType))
        setHasError(true)
        setLoading(false)
    }
}

export const handleMfaSubmit = async ({
    form,
    setLoading,
    setHasError,
    options: { dispatch, client },
    session,
    email,
}: {
    email: string
    session: string
    form: FormInstance
    setLoading: (loading: boolean) => void
    setHasError: (hasError: boolean) => void
    options: {
        dispatch: Dispatch
        client: ApolloClientType
    }
}) => {
    try {
        await form.validateFields([EMAIL_ADDRESS, PASSWORD])
    } catch (err) {
        return
    }

    setLoading(true)

    try {
        const { data: loginWith2faData } = await axios.post(
            `${GRAPHQL_API_REST_BASE}/verify2FA`,
            { email, session, code: form.getFieldValue(VERIFICATION_CODE) },
            { withCredentials: true },
        )

        if (loginWith2faData?.error) {
            throw loginWith2faData.data.console.error()
        }

        logUserIn({
            dispatch,
            client,
            loginData: loginWith2faData,
            setLoading,
            mfaEnabled: true,
        })
    } catch (err) {
        const authErrorMessage = (err as LoginErrorResponseType).message

        dispatch(setAuthError(`LoginPage:: ${authErrorMessage}`))
        setLoading(false)
        setHasError(true)
    }
}

// will write a lib function for this later
export const useExtractParams = (searchString: string) =>
    useMemo(() => {
        const urlParams = new URLSearchParams(searchString)
        const paramsObj: { [key: string]: string | null } = {}

        PARAMS.forEach(
            (param: string) => (paramsObj[param] = urlParams.get(param)),
        )

        return paramsObj
    }, [searchString])
